import chalk from 'chalk';
import ora from 'ora';
import type { CacheManager, CacheMeta, CacheStats } from './types';

/**
 * 缓存插件接口
 */
export interface CachePlugin {
  /** 插件名称 */
  name: string;
  /** 插件版本 */
  version: string;
  /** 插件描述 */
  description?: string;

  /** 初始化插件 */
  initialize?(config: any): Promise<void>;

  /** 插件销毁 */
  destroy?(): Promise<void>;

  /** 缓存操作钩子 */
  hooks?: {
    beforeGet?(key: string): Promise<void>;
    afterGet?(key: string, value: any): Promise<void>;
    beforeSet?(key: string, value: any): Promise<void>;
    afterSet?(key: string, value: any): Promise<void>;
    beforeDelete?(key: string): Promise<void>;
    afterDelete?(key: string): Promise<void>;
    beforeClear?(): Promise<void>;
    afterClear?(): Promise<void>;
  };

  /** 获取插件配置 */
  getConfig?(): any;

  /** 设置插件配置 */
  setConfig?(config: any): void;
}

/**
 * 缓存插件管理器
 * 提供插件的注册、管理和生命周期控制
 */
export class CachePluginManager {
  private plugins: Map<string, CachePlugin> = new Map();
  private pluginConfigs: Map<string, any> = new Map();
  private spinner = ora();

  constructor() {}

  /**
   * 注册插件
   */
  registerPlugin(plugin: CachePlugin): void {
    if (this.plugins.has(plugin.name)) {
      console.warn(chalk.yellow(`插件 ${plugin.name} 已存在，将被覆盖`));
    }

    this.plugins.set(plugin.name, plugin);
    console.log(chalk.green(`插件 ${plugin.name} v${plugin.version} 注册成功`));
  }

  /**
   * 卸载插件
   */
  async unregisterPlugin(pluginName: string): Promise<void> {
    const plugin = this.plugins.get(pluginName);
    if (!plugin) {
      console.warn(chalk.yellow(`插件 ${pluginName} 不存在`));
      return;
    }

    try {
      if (plugin.destroy) {
        await plugin.destroy();
      }
      this.plugins.delete(pluginName);
      this.pluginConfigs.delete(pluginName);
      console.log(chalk.green(`插件 ${pluginName} 卸载成功`));
    } catch (error) {
      console.error(chalk.red(`卸载插件 ${pluginName} 失败:`), error);
      throw error;
    }
  }

  /**
   * 初始化所有插件
   */
  async initializePlugins(): Promise<void> {
    this.spinner.start('初始化缓存插件...');

    try {
      const initPromises = Array.from(this.plugins.values()).map(
        async plugin => {
          if (plugin.initialize) {
            const config = this.pluginConfigs.get(plugin.name);
            await plugin.initialize(config);
          }
        }
      );

      await Promise.all(initPromises);
      this.spinner.succeed(`插件初始化完成 (${this.plugins.size} 个插件)`);
    } catch (error) {
      this.spinner.fail('插件初始化失败');
      console.error(chalk.red('初始化错误:'), error);
      throw error;
    }
  }

  /**
   * 销毁所有插件
   */
  async destroyPlugins(): Promise<void> {
    this.spinner.start('销毁缓存插件...');

    try {
      const destroyPromises = Array.from(this.plugins.values()).map(
        async plugin => {
          if (plugin.destroy) {
            await plugin.destroy();
          }
        }
      );

      await Promise.all(destroyPromises);
      this.spinner.succeed(`插件销毁完成 (${this.plugins.size} 个插件)`);
    } catch (error) {
      this.spinner.fail('插件销毁失败');
      console.error(chalk.red('销毁错误:'), error);
      throw error;
    }
  }

  /**
   * 执行钩子函数
   */
  async executeHook(
    hookName: keyof NonNullable<CachePlugin['hooks']>,
    ...args: any[]
  ): Promise<void> {
    for (const plugin of this.plugins.values()) {
      if (plugin.hooks && plugin.hooks[hookName]) {
        try {
          await (plugin.hooks[hookName] as any)(...args);
        } catch (error) {
          console.error(
            chalk.red(`插件 ${plugin.name} 钩子 ${hookName} 执行失败:`),
            error
          );
        }
      }
    }
  }

  /**
   * 获取插件列表
   */
  getPlugins(): Array<{
    name: string;
    version: string;
    description?: string;
    config?: any;
  }> {
    return Array.from(this.plugins.values()).map(plugin => ({
      name: plugin.name,
      version: plugin.version,
      description: plugin.description,
      config: this.pluginConfigs.get(plugin.name),
    }));
  }

  /**
   * 获取插件
   */
  getPlugin(name: string): CachePlugin | undefined {
    return this.plugins.get(name);
  }

  /**
   * 设置插件配置
   */
  setPluginConfig(pluginName: string, config: any): void {
    const plugin = this.plugins.get(pluginName);
    if (!plugin) {
      throw new Error(`插件 ${pluginName} 不存在`);
    }

    this.pluginConfigs.set(pluginName, config);

    if (plugin.setConfig) {
      plugin.setConfig(config);
    }
  }

  /**
   * 获取插件配置
   */
  getPluginConfig(pluginName: string): any {
    return this.pluginConfigs.get(pluginName);
  }

  /**
   * 检查插件是否存在
   */
  hasPlugin(pluginName: string): boolean {
    return this.plugins.has(pluginName);
  }

  /**
   * 获取插件统计信息
   */
  getPluginStats(): {
    totalPlugins: number;
    activePlugins: number;
    pluginNames: string[];
  } {
    return {
      totalPlugins: this.plugins.size,
      activePlugins: Array.from(this.plugins.values()).filter(
        plugin => plugin.hooks && Object.keys(plugin.hooks).length > 0
      ).length,
      pluginNames: Array.from(this.plugins.keys()),
    };
  }
}

/**
 * 缓存管理器包装器
 * 为缓存管理器添加插件支持
 */
export class CacheManagerWithPlugins implements CacheManager {
  private cacheManager: CacheManager;
  private pluginManager: CachePluginManager;

  constructor(cacheManager: CacheManager, pluginManager: CachePluginManager) {
    this.cacheManager = cacheManager;
    this.pluginManager = pluginManager;
  }

  /**
   * 获取缓存
   */
  async get(key: string): Promise<any> {
    await this.pluginManager.executeHook('beforeGet', key);

    try {
      const value = await this.cacheManager.get(key);
      await this.pluginManager.executeHook('afterGet', key, value);
      return value;
    } catch (error) {
      await this.pluginManager.executeHook('afterGet', key, null);
      throw error;
    }
  }

  /**
   * 设置缓存
   */
  async set(
    key: string,
    value: any,
    options?: Partial<CacheMeta>
  ): Promise<void> {
    await this.pluginManager.executeHook('beforeSet', key, value);

    try {
      await this.cacheManager.set(key, value, options);
      await this.pluginManager.executeHook('afterSet', key, value);
    } catch (error) {
      await this.pluginManager.executeHook('afterSet', key, value);
      throw error;
    }
  }

  /**
   * 删除缓存
   */
  async delete(key: string): Promise<void> {
    await this.pluginManager.executeHook('beforeDelete', key);

    try {
      await this.cacheManager.delete(key);
      await this.pluginManager.executeHook('afterDelete', key);
    } catch (error) {
      await this.pluginManager.executeHook('afterDelete', key);
      throw error;
    }
  }

  /**
   * 清空缓存
   */
  async clear(): Promise<void> {
    await this.pluginManager.executeHook('beforeClear');

    try {
      await this.cacheManager.clear();
      await this.pluginManager.executeHook('afterClear');
    } catch (error) {
      await this.pluginManager.executeHook('afterClear');
      throw error;
    }
  }

  /**
   * 批量获取缓存
   */
  async getBatch(keys: string[]): Promise<Map<string, any>> {
    // 为每个键执行钩子
    for (const key of keys) {
      await this.pluginManager.executeHook('beforeGet', key);
    }

    try {
      const results = await this.cacheManager.getBatch(keys);

      // 为每个结果执行钩子
      for (const [key, value] of results.entries()) {
        await this.pluginManager.executeHook('afterGet', key, value);
      }

      return results;
    } catch (error) {
      throw error;
    }
  }

  /**
   * 批量设置缓存
   */
  async setBatch(
    items: Array<{ key: string; value: any; options?: Partial<CacheMeta> }>
  ): Promise<void> {
    // 为每个项执行钩子
    for (const item of items) {
      await this.pluginManager.executeHook('beforeSet', item.key, item.value);
    }

    try {
      await this.cacheManager.setBatch(items);

      // 为每个项执行钩子
      for (const item of items) {
        await this.pluginManager.executeHook('afterSet', item.key, item.value);
      }
    } catch (error) {
      throw error;
    }
  }

  /**
   * 批量删除缓存
   */
  async deleteBatch(keys: string[]): Promise<void> {
    // 为每个键执行钩子
    for (const key of keys) {
      await this.pluginManager.executeHook('beforeDelete', key);
    }

    try {
      await this.cacheManager.deleteBatch(keys);

      // 为每个键执行钩子
      for (const key of keys) {
        await this.pluginManager.executeHook('afterDelete', key);
      }
    } catch (error) {
      throw error;
    }
  }

  /**
   * 事务性操作缓存
   */
  async setTransaction(
    operations: Array<{
      type: 'set' | 'delete';
      key: string;
      value?: any;
      options?: Partial<CacheMeta>;
    }>
  ): Promise<void> {
    // 为每个操作执行钩子
    for (const operation of operations) {
      if (operation.type === 'set') {
        await this.pluginManager.executeHook(
          'beforeSet',
          operation.key,
          operation.value
        );
      } else if (operation.type === 'delete') {
        await this.pluginManager.executeHook('beforeDelete', operation.key);
      }
    }

    try {
      await this.cacheManager.setTransaction(operations);

      // 为每个操作执行钩子
      for (const operation of operations) {
        if (operation.type === 'set') {
          await this.pluginManager.executeHook(
            'afterSet',
            operation.key,
            operation.value
          );
        } else if (operation.type === 'delete') {
          await this.pluginManager.executeHook('afterDelete', operation.key);
        }
      }
    } catch (error) {
      throw error;
    }
  }

  /**
   * 获取缓存统计
   */
  async getStats(): Promise<CacheStats> {
    return await this.cacheManager.getStats();
  }

  /**
   * 预热缓存
   */
  async warmup(keys: string[]): Promise<void> {
    return await this.cacheManager.warmup(keys);
  }

  /**
   * 清理过期缓存
   */
  async cleanup(): Promise<void> {
    return await this.cacheManager.cleanup();
  }

  /**
   * 导出缓存
   */
  async export(): Promise<Buffer> {
    return await this.cacheManager.export();
  }

  /**
   * 导入缓存
   */
  async import(data: Buffer): Promise<void> {
    return await this.cacheManager.import(data);
  }

  /**
   * 获取插件管理器
   */
  getPluginManager(): CachePluginManager {
    return this.pluginManager;
  }
}
